/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconSparklesSolid from '@apollo/icons/default/IconSparklesSolid.svg';
import { jsx } from '@emotion/react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { EmptyLayout } from 'src/app/account/championDashboardPage/widgets/EmptyLayout';
import { InsightsExpandButton } from 'src/app/graph/insights/InsightsExpandButton';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { InsightsEnforcementWarning } from 'src/components/insightsEnforcementWarning/InsightsEnforcementWarning';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { useLDFlag } from 'src/lib/launchDarkly';
import { mergeQueryParams } from 'src/lib/routing';

import { AllowedTabsProvider } from '../../operationsPage/allowedTabsProvider/AllowedTabsProvider';
import { useInsightsL3NavState } from '../../operationsPage/insightsL3Nav/useInsightsL3NavState';
import { SetupOperationMetrics } from '../../operationsPage/SetupOperationMetrics';
import { TimeRangeProvider } from '../../operationsPage/timeRangeProvider/TimeRangeProvider';
import { insightsRouteConfig } from '../../routes';

interface SubgraphMainContentProps {
  serviceId: string | undefined;
  accountId: string | undefined;
  reportingEnabled?: boolean;
  isSupergraphVariant: boolean;
}

export const SubgraphInsightsMainContent = ({
  serviceId,
  accountId,
  reportingEnabled,
  isSupergraphVariant,
}: SubgraphMainContentProps) => {
  const location = useLocation();
  const { isOpen, onOpen } = useInsightsL3NavState();
  const trackCustomEvent = useTrackCustomEvent();

  const { graphId, tab } = useRouteParams(insightsRouteConfig);

  const errorTabRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const curRef = errorTabRef.current;

    const handleClick = () => {
      if (tab !== 'errors') {
        trackCustomEvent({
          category: 'Subgraph Insights',
          action: 'header_error_tab_click',
        });
      }
    };
    if (curRef !== null) {
      curRef.addEventListener('click', handleClick);
    }

    return () => curRef?.removeEventListener('click', handleClick);
  }, [errorTabRef, tab, trackCustomEvent]);

  const showErrorCategorization =
    useLDFlag('insights-error-categorization-ui') && isSupergraphVariant;

  if (!serviceId) {
    return null;
  }

  const highlightNewFeatureTabs = showErrorCategorization ? ['errors'] : [];

  return (
    <TimeRangeProvider serviceId={graphId}>
      {() => (
        <AllowedTabsProvider isDemoPage={!accountId}>
          {({ clickableTabs, visibleTabs, defaultTab }) => {
            const activeTab = tab || defaultTab.value;
            return (
              <div className="flex flex-1 flex-col bg-primary">
                <InsightsEnforcementWarning />
                <div className="sticky top-0 z-20 border-b border-primary bg-primary px-8 pt-4">
                  <PageTitle
                    className="border-b-0 px-[0px]"
                    title="Subgraphs"
                    leftAdornment={
                      !isOpen ? (
                        <Tooltip label="Expand">
                          <InsightsExpandButton onOpen={onOpen} />
                        </Tooltip>
                      ) : null
                    }
                  />
                  {reportingEnabled && (
                    <div>
                      {visibleTabs.map((tabConfig) => {
                        const disabled = !clickableTabs.find(
                          (clickableTab) =>
                            clickableTab.value === tabConfig.value,
                        );
                        const maybeRefProp =
                          tabConfig.title === 'Errors'
                            ? { ref: errorTabRef }
                            : {};
                        return (
                          <HeaderTab
                            key={tabConfig.value}
                            isActive={() => activeTab === tabConfig.value}
                            tabID={tabConfig.value}
                            badge={
                              highlightNewFeatureTabs.includes(
                                tabConfig.value,
                              ) ? (
                                <Tooltip label="This page has new features to explore">
                                  <IconSparklesSolid className="icon-beta ml-0.5 opacity-60" />
                                </Tooltip>
                              ) : null
                            }
                            to={{
                              ...location,
                              search: mergeQueryParams(location.search, {
                                tab: tabConfig.value,
                              }),
                            }}
                            tabName={
                              disabled ? (
                                <Tooltip
                                  label={`Select an individual subgraph to view the ${tabConfig.title} tab.`}
                                >
                                  <span>{tabConfig.title}</span>
                                </Tooltip>
                              ) : (
                                tabConfig.title
                              )
                            }
                            disabled={disabled}
                            {...maybeRefProp}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="relative size-full px-8 pt-6">
                  <div className="flex justify-center">
                    {!reportingEnabled ? (
                      <SetupOperationMetrics />
                    ) : (
                      <EmptyLayout
                        title="Subgraph Insights are not yet supported"
                        body="Subgraphs are not yet supported in Insights. Please check back soon."
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          }}
        </AllowedTabsProvider>
      )}
    </TimeRangeProvider>
  );
};
